import React from 'react';
import DemoStage from '../DemoStage.jsx';

/**
 * Animated All Cases search: the cursor lands on the search box, a query is
 * typed character-block by character-block, and the mini case table narrows
 * live — exactly the CaseList behaviour (substring match on the lowercased
 * ref, so "AE-04" keeps only AE-042).
 *
 * Markup borrows the real classes (.search, td.case-id, .badge.status-*,
 * global table styles) so the demo can never drift from the actual look.
 * Placeholder data only.
 */

const ROWS = [
  { ref: 'AE-042', clinic: 'Clinic_A', cls: 'status-open', status: 'Open' },
  { ref: 'AE-017', clinic: 'Clinic_B', cls: 'status-new',  status: 'New' },
  { ref: 'AE-123', clinic: 'Clinic_C', cls: 'status-inv',  status: 'Pending' },
  { ref: 'AE-390', clinic: 'Clinic_A', cls: 'status-wait', status: 'Waiting on Client' },
];

// scene → the text sitting in the search box
const QUERY = { all: '', typed1: 'AE-0', typed2: 'AE-04', done: 'AE-04' };

const STEPS = [
  {
    caption: 'The search box sits above the table — it matches case ID, clinic, region, title and therapist.',
    scene: 'all', ms: 2400,
    cursor: 'search',
  },
  {
    caption: 'Start typing — the table narrows live with every character.',
    scene: 'typed1', ms: 2200,
    cursor: 'search', click: true,
  },
  {
    caption: 'A few characters of the reference are usually enough to pin one case.',
    scene: 'typed2', ms: 2200,
    cursor: 'search',
  },
  {
    caption: 'Click the row (or press Enter on it) to open the case.',
    scene: 'done', ms: 2800,
    cursor: 'row-AE-042', click: true,
  },
];

export default function SearchFilterDemo() {
  return (
    <DemoStage steps={STEPS} label="Searching the All Cases list" height={250}>
      {(step) => {
        const scene = step?.scene ?? 'all';
        const query = QUERY[scene] ?? '';
        const q = query.toLowerCase();
        const rows = ROWS.filter(r =>
          !q || r.ref.toLowerCase().includes(q) || r.clinic.toLowerCase().includes(q));
        return (
          <div className="kb-mock">
            <div className="search" data-kb-target="search" style={{ marginBottom: 12, maxWidth: 280 }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                readOnly
                tabIndex={-1}
                value={query}
                placeholder="Search by case ID, clinic, therapist…"
              />
            </div>
            <div className="table-scroll">
              <table aria-hidden="true">
                <thead>
                  <tr>
                    <th>Case ID</th>
                    <th>Clinic</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(r => (
                    <tr key={r.ref} data-kb-target={`row-${r.ref}`}>
                      <td className="case-id">{r.ref}</td>
                      <td>{r.clinic}</td>
                      <td>
                        <span className={`badge ${r.cls}`}><span className="dot" />{r.status}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      }}
    </DemoStage>
  );
}
